import { ListRenderItem } from '@shopify/flash-list';
import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import {
  NAVIGATION_HEADER_HEIGHT,
  NavigationHeader,
} from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { difference, intersection, isEqual, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doAseFieldInstanceAddManyExisting,
  doAseSetGroupFilter,
} from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted,
  selectAseFieldInstancesCountForGroup,
  selectAseTplFieldToGroup,
  selectAseFieldGroupFilter,
} from 'store/assessments/selectors';
import { IconButton } from 'components/IconButton';
import { View } from 'react-native';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';
import { RepeatableListItem } from './RepeatableListItem';

type Props = ModalScreenProps<ModalScreens.ASEREPEATABLEINSTANCENEWFROMLIST>;

export const AddRepeatableListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { groupId, parent } = route.params;
  const [selected, setSelected] = React.useState<string[]>([]);

  const filterKey = 'adding';
  const groupFilterKey = `${filterKey}-${groupId}`;
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const searchValue = groupFilter.search;

  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );

  // Get total number of instances in asset and in assessment
  const totalInstanceCount = useSelector((state) =>
    selectAseFieldInstancesCountForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: true,
    }),
  );

  const instanceIds = useSelector((state) =>
    selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
    }),
  );

  const filteredInstanceIds = useSelector((state) =>
    selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
      filtered: true,
    }),
  );

  const handlePressAdd = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      doAseFieldInstanceAddManyExisting({
        instanceIds: selected,
        parent,
      }),
    );
  };

  const unaddedCount = totalInstanceCount - groupStats.assessmentInstancesCount;

  const handlePressItem = React.useCallback(
    (instanceId: string) =>
      setSelected((current) =>
        current.includes(instanceId)
          ? without(current, instanceId)
          : [...current, instanceId],
      ),
    [],
  );

  const renderItem: ListRenderItem<string> = React.useCallback(
    ({ item, extraData }) => {
      if (item === 'existingend') {
        return (
          <Card
            margin={false}
            marginTop={false}
            style={{ marginTop: -theme.paddingHorizontalHalf }}
            explanation={
              parent
                ? pluralise(
                    unaddedCount,
                    `This ${group?.calculatedInfo.singularName.toLowerCase()} has not been linked to this ${parentGroup?.calculatedInfo.singularName.toLowerCase()} yet`,
                    `These ${group?.name.toLowerCase()} have not been linked to this ${parentGroup?.calculatedInfo.singularName.toLowerCase()} yet`,
                  )
                : pluralise(
                    unaddedCount,
                    `This ${group?.calculatedInfo.singularName.toLowerCase()} exists on the asset but has not been added to the assessment yet`,
                    `These ${group?.name.toLowerCase()} exist on the asset but have not been added to the assessment yet`,
                  )
            }
          />
        );
      }
      return (
        <RepeatableListItem
          instanceId={item}
          onPress={handlePressItem}
          checkedValue={selected.includes(item)}
          parent={parent}
          disableAddButton={!!extraData.listHasSelection}
          from="add"
        />
      );
    },
    [
      handlePressItem,
      selected,
      parent,
      unaddedCount,
      group?.calculatedInfo.singularName,
      group?.name,
      parentGroup?.calculatedInfo.singularName,
    ],
  );

  const handleClose = React.useCallback(() => {
    if (selected.length) {
      confirm({
        text: `The chosen ${pluralise(
          selected.length,
          group?.calculatedInfo.singularName.toLowerCase() || 'item',
          group?.name.toLowerCase() || 'items',
        )} will not be added`,
        confirmText: 'Okay',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [
    confirm,
    dispatch,
    group?.calculatedInfo.singularName,
    group?.name,
    selected.length,
  ]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !selected.length });
  }, [navigation, selected.length]);
  useEsc(handleClose);

  React.useEffect(() => {
    if (!unaddedCount) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, unaddedCount]);

  React.useEffect(() => {
    const newSelected = difference(selected, groupStats.assessmentInstanceIds);
    if (!isEqual(selected, newSelected)) {
      setSelected(newSelected);
    }
  }, [groupStats.assessmentInstanceIds, selected]);

  const handleSearch = React.useCallback(
    (search: string) => {
      dispatch(
        doAseSetGroupFilter({
          groupId: groupFilterKey,
          data: { ...groupFilter, search },
        }),
      );
    },
    [dispatch, groupFilter, groupFilterKey],
  );

  const finalInstanceIds = intersection(instanceIds, [
    ...filteredInstanceIds,
    ...selected,
  ]);

  if (!group) {
    return null;
  }

  const hasEnabledFilterOptions = !!Object.keys(groupFilter.checks).length;

  return (
    <PageFlatList
      header={
        <ViewWithSeparators>
          <NavigationHeader
            heading={`${pluralise(
              finalInstanceIds.length,
              `1 ${group.calculatedInfo.singularName}`,
              `{x} ${group.name}`,
            )}${searchValue ? ' (Filtered)' : ''}`}
            subheading={
              selected.length
                ? `${selected.length} Selected`
                : parentGroup
                  ? `Add to Assessment and Link to ${parentGroup.calculatedInfo.singularName}`
                  : 'Add to Assessment'
            }
            onPressCancelButton={handleClose}
            onPressSaveButton={selected.length ? handlePressAdd : null}
            saveButtonText="Add"
          />
          <View
            style={{
              flexDirection: 'row',
              paddingHorizontal: theme.paddingHorizontal,
              minHeight: NAVIGATION_HEADER_HEIGHT,
              alignItems: 'center',
            }}
          >
            <ListSelectAllCheckbox
              onSelectAll={() => setSelected(finalInstanceIds)}
              onSelectNone={() => setSelected([])}
              selectionState={
                selected.length
                  ? selected.length !== finalInstanceIds.length
                    ? 'some'
                    : 'all'
                  : 'none'
              }
            />
            <TextField
              clearable
              indentActions
              onChange={handleSearch}
              value={searchValue}
              placeholder={`Search ${group.name}`}
              iconLeft={ICONS.MagnifyingGlassLight}
              onChangeDebounceMs={300}
              inputStyle={{ minHeight: 48 }}
              containerStyle={{
                minHeight: 48,
                flex: 1,
                backgroundColor: 'transparent',
              }}
              autoFocusWeb
              autoFocusDelay
              onEsc={handleClose}
            />
            <ViewWithSeparators
              horizontal
              separatorMargin={theme.paddingHorizontal / 2}
              separatorMarginOther={theme.paddingHorizontal / 2}
              style={{ alignItems: 'center' }}
            >
              <View />
              <IconButton
                icon={ICONS.SlidersUpLight}
                onPress={() =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.AseFieldInstancesOptions,
                      params: { groupId, listType: 'adding' },
                    }),
                  )
                }
                showBadge={hasEnabledFilterOptions}
              />
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Button
                  text="All"
                  compact
                  buttonTheme={ButtonTheme.themeless}
                  onPress={
                    selected.length !== finalInstanceIds.length
                      ? () => setSelected(finalInstanceIds)
                      : null
                  }
                />
                <Button
                  text="None"
                  compact
                  buttonTheme={ButtonTheme.themeless}
                  onPress={selected.length ? () => setSelected([]) : null}
                />
              </View>
            </ViewWithSeparators>
          </View>
        </ViewWithSeparators>
      }
      contentPaddingTop={theme.paddingPageVertical}
      listConfig={{
        data: [...finalInstanceIds, 'existingend'],
        extraData: { listHasSelection: !!selected.length },
        keyExtractor: (instanceId) => instanceId,
        renderItem,
      }}
    />
  );
};
